import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { notification, Tooltip, Select, Space, Alert, Button } from 'antd';
import { AuthContext } from '../../../../context/authContext';
import axiosConfig from '../../../../config/axiosConfig';
import { useFeatureFlag, FEATURE_KEYS } from '../../../../utils/planFeatures';
import { aiConfigApi } from '../../../aiChatbot/services/aiChatbotApi';
import CampaignAIBehaviourPanel from './CampaignAIBehaviourPanel';
import './NewTemplateBroadcastPage.css';

const NewTemplateBroadcastPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // eslint-disable-next-line no-unused-vars
  const { currentUser } = useContext(AuthContext);
  const { enabled: byoEnabled } = useFeatureFlag(FEATURE_KEYS.BYO_WEBHOOK);

  // Get template from navigation state
  const template = location.state?.template;
  
  // State management
  const [addressBooks, setAddressBooks] = useState([]);
  const [selectedAddressBook, setSelectedAddressBook] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [labelFilter, setLabelFilter] = useState([]);
  const [availableLabels, setAvailableLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [pageSize] = useState(20);
  
  // Variable mapping states
  const [showVariableMapping, setShowVariableMapping] = useState(false);
  const [templateVariables, setTemplateVariables] = useState([]);
  const [addressBookColumns, setAddressBookColumns] = useState([]);
  const [variableMapping, setVariableMapping] = useState({});
  // Dynamic media header mapping — stores the addressbook column key whose value is a media URL
  const [headerMediaColumn, setHeaderMediaColumn] = useState('');
  
  // Campaign creation state
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [campaignName, setCampaignName] = useState('');

  // ── AI Behaviour (per-campaign override) ──────────────────────────────
  // When a recipient replies, the AI uses this campaign's skills/KB scope
  // (sticky for 24h). is_one_way=true suppresses any auto-reply entirely.
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiSkills, setAiSkills] = useState([]); // [{skill_key, display_name, ...}]
  const [aiBehaviour, setAiBehaviour] = useState({
    skill_keys: [],
    kb_tags: [],
    is_one_way: false,
    custom_system_prompt: '',
  });
  
  // Error and progress states
  const [errors, setErrors] = useState({
    campaignName: '',
    template: '',
    addressBook: '',
    contacts: '',
    variableMapping: '',
    api: ''
  });
  const [completedSteps, setCompletedSteps] = useState({
    addressBookSelected: false,
    contactsSelected: false,
    variableMappingComplete: false,
    campaignNameEntered: false
  });
  
  // Mobile responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileStep, setMobileStep] = useState(0); // 0: Address Book, 1: Contacts, 2: Preview/Mapping, 3: Create
  
  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load skill catalog (custom + starter templates) once for the AI panel.
  useEffect(() => {
    let alive = true;
    aiConfigApi.listSkills().then((r) => {
      if (!alive) return;
      const all = r?.data?.entity?.all || r?.data?.entity || [];
      setAiSkills(Array.isArray(all) ? all : []);
    }).catch(() => { /* AI may not be enabled — silent */ });
    return () => { alive = false; };
  }, []);
  
  // Helper functions for error and progress management
  const clearError = (field) => {
    setErrors(prev => ({ ...prev, [field]: '' }));
  };
  
  const setError = (field, message) => {
    setErrors(prev => ({ ...prev, [field]: message }));
  };
  
  const updateProgress = (step, completed) => {
    setCompletedSteps(prev => ({ ...prev, [step]: completed }));
  };
  
  // eslint-disable-next-line no-unused-vars
  const validateStep = (step) => {
    switch (step) {
      case 'addressBookSelected':
        return selectedAddressBook !== null;
      case 'contactsSelected':
        return selectedContacts.length > 0;
      case 'variableMappingComplete':
        return templateVariables.length === 0 || Object.keys(variableMapping).length === templateVariables.length;
      case 'campaignNameEntered':
        return campaignName.trim() !== '';
      default:
        return false;
    }
  };
  
  // eslint-disable-next-line no-unused-vars
  const getStepMessage = (step) => {
    const messages = {
      addressBookSelected: 'Select an address book to continue',
      contactsSelected: 'Select at least one contact from the address book',
      variableMappingComplete: 'Map all template variables to address book columns',
      campaignNameEntered: 'Enter a campaign name'
    };
    return messages[step] || '';
  };

  // Computed values
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.mobileno?.includes(searchTerm);
    
    // Filter by labels (supports JSON array strings, single string, or array)
    let matchesLabel = true;
    if (labelFilter && labelFilter.length > 0) {
      const selected = labelFilter.map(l => l.toLowerCase());
      const contactLabels = [];
      
      const labelFields = ['label', 'labels', 'userdata_labels', 'userlabels'];
      labelFields.forEach(field => {
        if (contact[field]) {
          const val = contact[field];
          if (typeof val === 'string') {
            const s = val.trim();
            let parsed = null;
            if (s.startsWith('[') && s.endsWith(']')) {
              try { parsed = JSON.parse(s); } catch (e) { parsed = null; }
            }
            if (Array.isArray(parsed)) {
              contactLabels.push(...parsed);
            } else {
              contactLabels.push(...s.split(',').map(l => l.trim()).filter(Boolean));
            }
          } else if (Array.isArray(val)) {
            contactLabels.push(...val);
          }
        }
      });
      
      if (contactLabels.length === 0) {
        matchesLabel = false;
      } else {
        const lowerContactLabels = contactLabels.map(l => String(l).toLowerCase());
        matchesLabel = selected.every(sel => lowerContactLabels.includes(sel));
      }
    }
    
    return matchesSearch && matchesLabel;
  });
  
  // Predefined labels for filtering (based on actual API response)
  const predefinedLabels = [
    { value: 'favourite', label: 'Favourite', color: '#ff4d4f' },
    { value: 'imp', label: 'Important', color: '#fa8c16' },
    { value: 'close', label: 'Close Friend', color: '#52c41a' },
    { value: 'family', label: 'Family', color: '#1890ff' },
    { value: 'business', label: 'Business', color: '#722ed1' },
    { value: 'vip', label: 'VIP Customer', color: '#9254de' }
  ];

  // Extract labels from contacts data - handles custom labels
  const extractLabelsFromContacts = (contacts) => {
    const labelSet = new Set();
    
    contacts.forEach(contact => {
      // Check various label fields - including 'label' from API response
      const labelFields = ['label', 'labels', 'userdata_labels', 'userlabels'];
      
      labelFields.forEach(field => {
        if (contact[field]) {
          let labels = [];
          
          try {
            if (typeof contact[field] === 'string') {
              // Handle JSON array string like ["favourite","close"]
              if (contact[field].startsWith('[') && contact[field].endsWith(']')) {
                labels = JSON.parse(contact[field]).filter(Boolean);
              }
              // Handle comma-separated string or single label
              else {
                labels = contact[field].split(',').map(label => label.trim()).filter(Boolean);
              }
            } else if (Array.isArray(contact[field])) {
              // Handle array
              labels = contact[field].filter(Boolean);
            }
          } catch (e) {
            // If JSON parsing fails, treat as regular string
            if (typeof contact[field] === 'string') {
              labels = contact[field].split(',').map(label => label.trim()).filter(Boolean);
            }
          }
          
          labels.forEach(label => labelSet.add(label));
        }
      });
    });
    
    return Array.from(labelSet).sort();
  };

  // Extract variables from template content - returns WITH braces like {{1}}, {{name}}
  const extractVariables = (text) => {
    if (!text) return [];
    const regex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      const variable = `{{${match[1]}}}`; // Include braces like AdvancedBroadcastBuilder
      if (!variables.includes(variable)) {
        variables.push(variable);
      }
    }
    // Sort numeric variables in order
    return variables.sort((a, b) => {
      const numA = parseInt(a.replace(/[{}]/g, ''));
      const numB = parseInt(b.replace(/[{}]/g, ''));
      if (isNaN(numA) || isNaN(numB)) return 0;
      return numA - numB;
    });
  };

  // Detect if the template has a media header (document / image / video) and return the type
  const getTemplateHeaderType = (tmpl) => {
    if (!tmpl) return null;
    const header = (tmpl.component || []).find(c => c.type === 'HEADER');
    if (header) {
      const fmt = (header.format || '').toUpperCase();
      if (['IMAGE', 'DOCUMENT', 'VIDEO'].includes(fmt)) return fmt.toLowerCase();
    }
    // Also handle flat fields some API responses use
    const direct = (tmpl.headertype || tmpl.header_type || tmpl.headerformat || tmpl.header_format || '').toUpperCase();
    if (['IMAGE', 'DOCUMENT', 'VIDEO'].includes(direct)) return direct.toLowerCase();
    return null;
  };

  // Derived — recalculated whenever template changes
  const templateHeaderType = getTemplateHeaderType(template);

  // Determine variable type (number for {{1}}, {{2}} or name for {{name}}, {{xyz}})
  const determineVariableType = (variables) => {
    if (variables.length === 0) return 'number';
    const isNumeric = variables.every(variable => {
      const match = variable.match(/\{\{(\d+)\}\}/);
      return match !== null;
    });
    return isNumeric ? 'number' : 'name';
  };

  // Fetch address book columns when address book is selected
  const fetchAddressBookColumns = async (bookId) => {
    try {
      const response = await axiosConfig.post('/addressbook/users', {
        bookid: bookId,
        limit: 10,
        offset: 0,
        searchby: "",
        labelfilter: ""
      });
      
      if (response.data.status && response.data.entity && response.data.entity.users) {
        const users = response.data.entity.users;
        
        // Extract column names from the first user
        if (users.length > 0) {
          const firstUser = users[0];
          const columns = [];
          
          // Add standard fields
          const standardFields = ['username', 'mobileno', 'emailid', 'address'];
          standardFields.forEach(field => {
            if (firstUser.hasOwnProperty(field)) {
              columns.push({
                key: field,
                label: field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                value: field,
                source: 'standard'
              });
            }
          });
          
          // Add userdata fields (flattened for template variables)
          if (firstUser.userdata && typeof firstUser.userdata === 'object') {
            Object.keys(firstUser.userdata).forEach(key => {
              columns.push({
                key: key,
                label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
                value: key,
                source: 'userdata'
              });
            });
          }
          
          setAddressBookColumns(columns);
        }
      }
    } catch (error) {
      console.error('Error fetching address book columns:', error);
    }
  };

  // Redirect if no template provided
  useEffect(() => {
    if (!template) {
      navigate('/dashboard/templates');
    }
  }, [template, navigate]);

  // Fetch address books on component mount
  useEffect(() => {
    fetchAddressBooks();
  }, []);

  // Extract variables from template when template changes
  useEffect(() => {
    if (template) {
      const content = formatTemplateContent(template);
      const variables = extractVariables(content);
      setTemplateVariables(variables);
      
      // Initialize variable mapping
      const initialMapping = {};
      variables.forEach(variable => {
        initialMapping[variable] = '';
      });
      setVariableMapping(initialMapping);
    }
  }, [template]);

  // Fetch contacts when address book is selected
  useEffect(() => {
    if (selectedAddressBook) {
      fetchContacts();
      fetchAddressBookColumns(selectedAddressBook.bookid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAddressBook, searchTerm, labelFilter, currentPage]);

  // Extract available labels from contacts whenever contacts data changes
  useEffect(() => {
    if (contacts && contacts.length > 0) {
      const extracted = extractLabelsFromContacts(contacts);
      setAvailableLabels(extracted);
    }
  }, [contacts]);

  // Progress tracking useEffect hooks
  useEffect(() => {
    updateProgress('addressBookSelected', selectedAddressBook !== null);
    if (selectedAddressBook) {
      clearError('addressBook');
    }
  }, [selectedAddressBook]);

  useEffect(() => {
    updateProgress('contactsSelected', selectedContacts.length > 0);
    if (selectedContacts.length > 0) {
      clearError('contacts');
    }
  }, [selectedContacts]);

  useEffect(() => {
    const bodyVarsMapped = templateVariables.length === 0 || 
                          (Object.keys(variableMapping).length === templateVariables.length &&
                           Object.values(variableMapping).every(value => value !== ''));
    // If the template has a media header, the user must also pick which column holds the URL
    const headerMapped = !templateHeaderType || !!headerMediaColumn;
    const isComplete = bodyVarsMapped && headerMapped;
    updateProgress('variableMappingComplete', isComplete);
    if (isComplete) {
      clearError('variableMapping');
    }
  }, [templateVariables, variableMapping, templateHeaderType, headerMediaColumn]);

  useEffect(() => {
    updateProgress('campaignNameEntered', campaignName.trim() !== '');
    if (campaignName.trim() !== '') {
      clearError('campaignName');
    }
  }, [campaignName]);

  // API Functions
  const fetchAddressBooks = async () => {
    try {
      setLoading(true);
      const response = await axiosConfig.get('/addressbook/details');
      
      if (response.data?.statuscode === 200 && response.data?.entity) {
        setAddressBooks(response.data.entity);
      } else {
        setAddressBooks([]);
      }
    } catch (error) {
      console.error('Error fetching address books:', error);
      setAddressBooks([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    if (!selectedAddressBook?.bookid) return;

    try {
      setContactsLoading(true);
      
      // Check if any custom labels are selected
      const hasCustomLabels = labelFilter.some(label => 
        !predefinedLabels.some(p => p.value === label)
      );
      
      // If custom labels are selected, fetch all contacts and filter client-side
      // Otherwise, use pagination with predefined labels
      const fetchLimit = hasCustomLabels ? 10000 : pageSize;
      const fetchOffset = hasCustomLabels ? 0 : (currentPage - 1) * pageSize;
      
      // Only send predefined labels to API, custom labels will be filtered client-side
      const predefinedLabelValues = labelFilter.filter(label => 
        predefinedLabels.some(p => p.value === label)
      );
      
      const requestBody = {
        bookid: selectedAddressBook.bookid,
        limit: fetchLimit,
        offset: fetchOffset,
        searchby: searchTerm || "",
        labelfilter: Array.isArray(predefinedLabelValues) && predefinedLabelValues.length > 0 ? predefinedLabelValues.join(',') : ""
      };

      const response = await axiosConfig.post('/addressbook/users', requestBody);
      
      if ((response.data?.statuscode === 200 || response.data?.status === true) && response.data?.entity) {
        const entity = response.data.entity;
        const contactsData = entity.users || entity || [];
        
        setContacts(contactsData);
        setTotalContacts(entity.totalRecords || contactsData.length);
      } else {
        setContacts([]);
        setTotalContacts(0);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      setContacts([]);
      setTotalContacts(0);
    } finally {
      setContactsLoading(false);
    }
  };

  // Event Handlers
  const handleAddressBookSelect = (addressBook) => {
    setSelectedAddressBook(addressBook);
    setSelectedContacts([]);
    setCurrentPage(1);
    setShowVariableMapping(false); // Reset variable mapping when changing address book
    // Clear address book error
    setErrors(prev => ({ ...prev, addressBook: '' }));
  };

  // Handle variable mapping
  const handleVariableMapping = (variable, columnKey) => {
    setVariableMapping(prev => ({
      ...prev,
      [variable]: columnKey
    }));
    // Clear variable mapping error when user starts mapping
    setErrors(prev => ({ ...prev, variableMapping: '' }));
  };

  // Toggle variable mapping modal
  const toggleVariableMapping = () => {
    setShowVariableMapping(!showVariableMapping);
  };

  const handleContactSelect = (contact) => {
    const isSelected = selectedContacts.some(c => c.userid === contact.userid);
    if (isSelected) {
      setSelectedContacts(selectedContacts.filter(c => c.userid !== contact.userid));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
      // Clear contacts error when at least one contact is selected
      setErrors(prev => ({ ...prev, contacts: '' }));
    }
  };

  const handleSelectAll = () => {
    // Check against filteredContacts instead of contacts to properly handle filtered list
    const allFilteredSelected = filteredContacts.length > 0 && 
      filteredContacts.every(fc => selectedContacts.some(sc => sc.userid === fc.userid));
    
    if (allFilteredSelected) {
      // Remove only the filtered contacts from selection
      setSelectedContacts(selectedContacts.filter(
        sc => !filteredContacts.some(fc => fc.userid === sc.userid)
      ));
    } else {
      // Add filtered contacts that aren't already selected
      const newSelections = filteredContacts.filter(
        fc => !selectedContacts.some(sc => sc.userid === fc.userid)
      );
      setSelectedContacts([...selectedContacts, ...newSelections]);
      // Clear contacts error when contacts are selected
      setErrors(prev => ({ ...prev, contacts: '' }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleLabelFilterChange = (selectedLabels) => {
    setLabelFilter(selectedLabels);
    setCurrentPage(1);
  };

  // Campaign creation functions
  const buildCampaignRequestBody = (campaignName) => {
    const baseBody = {
      campaignname: campaignName,
      templateid: template.templateid || template.id
    };

    // Add address book information if available
    if (selectedAddressBook?.bookid) {
      baseBody.bookid = selectedAddressBook.bookid;
    }

    // Add template mapping if variables exist and are mapped
    const filteredBodyMapping = Object.fromEntries(
      Object.entries(variableMapping).filter(([, value]) => value !== '')
    );
    const hasBodyMapping = Object.keys(filteredBodyMapping).length > 0;
    const hasHeaderMapping = templateHeaderType && headerMediaColumn;

    if (hasBodyMapping || hasHeaderMapping) {
      baseBody.templatemapping = {
        variabletype: determineVariableType(templateVariables),
        mapping: filteredBodyMapping,
      };
      // Attach dynamic media header info so backend can look up the URL per-contact
      if (hasHeaderMapping) {
        baseBody.templatemapping.headerMediaColumn = headerMediaColumn;
        baseBody.templatemapping.headerMediaType = templateHeaderType;
      }
    }

    // For NewTemplateBroadcastPage, we always use 'selection' type with specific user IDs
    return {
      ...baseBody,
      selectionType: 'selection',
      userids: selectedContacts.map(contact => contact.userid) || []
    };
  };

  // Build contactsdetails grouped by country code for rate calculation
  const buildContactsDetails = () => {
    const counts = {};
    (selectedContacts || []).forEach(contact => {
      const rawCode = contact?.countrycode || '91';
      const code = rawCode.startsWith('+') ? rawCode : `+${rawCode}`;
      counts[code] = (counts[code] || 0) + 1;
    });
    return Object.entries(counts).map(([countrycode, count]) => ({
      countrycode,
      count: String(count)
    }));
  };

  const validateCampaignRequest = (requestBody) => {
    if (!requestBody.campaignname || requestBody.campaignname.trim() === '') {
      return { isValid: false, message: 'Campaign name is required' };
    }
    if (!requestBody.templateid) {
      return { isValid: false, message: 'Template selection is required' };
    }
    if (!requestBody.userids || requestBody.userids.length === 0) {
      return { isValid: false, message: 'At least one contact must be selected' };
    }
    if (!selectedAddressBook?.bookid) {
      return { isValid: false, message: 'Address book selection is required' };
    }
    
    return { isValid: true };
  };

  const handleCreateCampaign = async () => {
    // Clear previous errors
    setError('api', '');
    setError('campaignName', '');
    setError('contacts', '');
    
    if (selectedContacts.length === 0) {
      setError('contacts', 'Please select at least one contact');
      return;
    }

    // Use state-based campaign name
    if (!campaignName || campaignName.trim() === '') {
      setError('campaignName', 'Please enter a campaign name');
      return;
    }

    try {
      setCreatingCampaign(true);
      
      // Build request body based on selection type
      const requestBody = buildCampaignRequestBody(campaignName.trim());

      // Calculate ratedetails before create, using selected contacts and template category
      const contactsdetails = buildContactsDetails();
      if (contactsdetails.length > 0) {
        const templateCategory = (template?.templatecategory || template?.category || 'marketing').toLowerCase();
        try {
          const rateRes = await axiosConfig.post('/whatsappcost/calculate', {
            category: templateCategory,
            contactsdetails
          });
          if (rateRes?.data?.status && rateRes?.data?.entity) {
            // Attach ratedetails including finalPrice and totalContacts
            requestBody.ratedetails = rateRes.data.entity;
          }
        } catch (rateErr) {
          console.error('Rate calculation failed, proceeding without ratedetails:', rateErr);
        }
      }

      // Validate the request body
      const validation = validateCampaignRequest(requestBody);
      if (!validation.isValid) {
        setError('api', validation.message);
        return;
      }

      console.log('Creating campaign with data:', requestBody);
      
      // API call to create campaign
      const response = await axiosConfig.post('/campaign/create', requestBody);
      
      if (response.data.statuscode === 200) {
        // Persist per-campaign AI behaviour override (best-effort — a failure
        // here doesn't fail the campaign create; user can edit later).
        const newCampaignId = response.data?.entity?.campaign?.campaignid
          || response.data?.entity?.campaignid;
        const hasAIOverride = aiBehaviour.is_one_way
          || (aiBehaviour.skill_keys && aiBehaviour.skill_keys.length > 0)
          || (aiBehaviour.kb_tags && aiBehaviour.kb_tags.length > 0)
          || (aiBehaviour.custom_system_prompt && aiBehaviour.custom_system_prompt.trim());
        if (newCampaignId && hasAIOverride) {
          try {
            await aiConfigApi.upsertCampaignAIConfig(newCampaignId, {
              skill_keys: aiBehaviour.skill_keys || [],
              kb_tags: aiBehaviour.kb_tags || [],
              is_one_way: !!aiBehaviour.is_one_way,
              custom_system_prompt: (aiBehaviour.custom_system_prompt || '').trim() || null,
            });
          } catch (aiErr) {
            console.warn('[AI Behaviour] save failed:', aiErr);
            notification.warning({
              message: 'Campaign created, but AI behaviour was not saved',
              description: 'You can re-configure it from the campaign details page.',
              duration: 5,
            });
          }
        }
        // Clear all errors on success
        setErrors({
          campaignName: '',
          template: '',
          addressBook: '',
          contacts: '',
          variableMapping: '',
          api: ''
        });
        
        // Show success notification
        notification.success({
          message: 'Campaign Created',
          description: `"${campaignName}" is ready to send`,
          duration: 3,
          placement: 'topRight',
        });
        
        // Navigate to broadcast page to see the created campaign
        setTimeout(() => navigate('/dashboard/broadcast'), 1500);
      } else {
        setError('api', response.data?.message || 'Failed to create campaign');
      }
      
    } catch (error) {
      console.error('Error creating campaign:', error);
      if (error.response?.data?.message) {
        setError('api', error.response.data.message);
      } else {
        setError('api', 'Failed to create campaign. Please try again.');
      }
    } finally {
      setCreatingCampaign(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleBack = () => {
    navigate('/dashboard/templates');
  };

  // Helper function to format template content for preview
  const formatTemplateContent = (template) => {
    if (!template) return '';
    
    // Get content from either templatecontent (API templates) or content (pre-approved templates)
    let content = template.templatecontent || template.content;
    
    // If no direct content, try to extract from component structure
    if (!content && template.component && template.component.length > 0) {
      const bodyComponent = template.component.find(comp => comp.type === 'BODY');
      content = bodyComponent?.text || '';
    }
    
    // Return content as-is, preserving template variables like {{1}}, {{name}}, etc.
    return content || '';
  };

  // Helper to build preview with actual mapped values
  const getPreviewContentWithMapping = (template) => {
    let content = formatTemplateContent(template);
    if (!content) return '';

    const sampleContact = selectedContacts[0] || contacts[0];
    if (sampleContact && variableMapping && Object.keys(variableMapping).length > 0) {
      Object.entries(variableMapping).forEach(([variable, columnName]) => {
        if (!columnName) return;
        let columnValue;

        if (sampleContact.userdata && typeof sampleContact.userdata === 'object' && sampleContact.userdata.hasOwnProperty(columnName)) {
          columnValue = sampleContact.userdata[columnName];
        } else if (sampleContact[`userdata_${columnName}`]) {
          columnValue = sampleContact[`userdata_${columnName}`];
        } else {
          columnValue = sampleContact[columnName];
        }

        // Variable now includes braces like {{1}}, so escape it properly for regex
        const safeVar = variable.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        content = content.replace(new RegExp(safeVar, 'g'), columnValue ?? '');
      });
    }

    return content;
  };

  // Helper function to get template buttons
  const getTemplateButtons = (template) => {
    if (!template) return [];
    
    // Check for direct buttons property
    if (template.buttons) return template.buttons;
    
    // Check for buttons in component structure
    if (template.component && template.component.length > 0) {
      const buttonComponent = template.component.find(comp => comp.type === 'BUTTONS');
      return buttonComponent?.buttons || [];
    }
    
    return [];
  };

  // Debug template data
  useEffect(() => {
    if (template) {
      console.log('Template data received:', template);
      console.log('Template content:', template.templatecontent);
      console.log('Template name:', template.templatename);
    }
  }, [template]);

  const totalPages = Math.ceil(totalContacts / pageSize);

  if (!template) {
    return (
      <div className="broadcast-page">
        <div className="broadcast-header">
          <button 
            onClick={() => navigate('/dashboard/templates')}
            className="back-btn"
          >
            ← Back
          </button>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  placeholder="Campaign name"
                  value={campaignName}
                  onChange={(e) => {
                    setCampaignName(e.target.value);
                    // Clear campaign name error when user starts typing
                    if (e.target.value.trim()) {
                      setErrors(prev => ({ ...prev, campaignName: '' }));
                    }
                  }}
                  style={{ 
                    padding: '8px 12px', 
                    border: errors.campaignName ? '1px solid #ef4444' : '1px solid #e5e7eb', 
                    borderRadius: 8,
                    minWidth: '200px'
                  }}
                />
                {errors.campaignName && (
                  <div style={{ 
                    position: 'absolute', 
                    top: '100%', 
                    left: 0, 
                    color: '#ef4444', 
                    fontSize: '12px', 
                    marginTop: '4px',
                    whiteSpace: 'nowrap'
                  }}>
                    {errors.campaignName}
                  </div>
                )}
              </div>
              <button 
                onClick={handleCreateCampaign}
                disabled={creatingCampaign || selectedContacts.length === 0 || !campaignName.trim()}
                className={`broadcast-btn ${selectedContacts.length > 0 && campaignName.trim() ? 'enabled' : 'disabled'}`}
                title="Please select a template from the templates page first"
              >
                {creatingCampaign ? 'Creating...' : `Create Campaign (${selectedContacts.length} contacts)`}
              </button>
            </div>
            
            {/* Error message for no template */}
            <div style={{ 
              color: '#ef4444', 
              fontSize: '12px', 
              textAlign: 'right'
            }}>
              Please select a template to continue
            </div>
          </div>
        </div>
        <div className="broadcast-content">
          <div className="empty-state">
            <div className="empty-content">
              <i className="bi bi-file-text empty-icon"></i>
              <h3 className="empty-title">No Template Selected</h3>
              <p className="empty-description">Please select a template from the templates page to create a broadcast.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate current step
  const getCurrentStep = () => {
    if (!selectedAddressBook) return 0;
    if (selectedContacts.length === 0) return 1;
    if (templateVariables.length > 0 && !completedSteps.variableMappingComplete) return 2;
    if (!campaignName.trim()) return 3;
    return 4;
  };

  const currentStep = getCurrentStep();

  return (
    <div className="broadcast-page">
      {/* Header */}
      <div className="broadcast-header">
        <button 
          onClick={() => navigate('/dashboard/templates')}
          className="back-btn"
        >
          ← Back
        </button>
        
        <h1 className="header-title">Create Campaign</h1>
        
        <div style={{ display: isMobile && mobileStep !== 3 ? 'none' : 'flex', flexDirection: 'column', alignItems: isMobile ? 'stretch' : 'flex-end', gap: 8, width: isMobile ? '100%' : 'auto', padding: isMobile ? '0 16px' : '0' }}>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', gap: 8 }}>
            {!isMobile ? (
              <Tooltip
                title={currentStep === 3 ? "👉 Step 4: Enter a campaign name" : null}
                placement="bottomRight"
                open={currentStep === 3}
                color="#3b82f6"
                overlayInnerStyle={{ fontSize: '13px', padding: '8px 12px' }}
              >
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    placeholder="Campaign name"
                    value={campaignName}
                    onChange={(e) => {
                      setCampaignName(e.target.value);
                      // Clear campaign name error when user starts typing
                      if (e.target.value.trim()) {
                        setErrors(prev => ({ ...prev, campaignName: '' }));
                      }
                    }}
                    style={{ 
                      padding: '8px 12px', 
                      border: currentStep === 3 ? '2px solid #3b82f6' : errors.campaignName ? '1px solid #ef4444' : '1px solid #e5e7eb', 
                      borderRadius: 8,
                      minWidth: '200px',
                      boxShadow: currentStep === 3 ? '0 0 6px rgba(59, 130, 246, 0.15)' : 'none',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {errors.campaignName && (
                    <div style={{ 
                      position: 'absolute', 
                      top: '100%', 
                      left: 0, 
                      color: '#ef4444', 
                      fontSize: '12px', 
                      marginTop: '4px',
                      whiteSpace: 'nowrap'
                    }}>
                      {errors.campaignName}
                    </div>
                  )}
                </div>
              </Tooltip>
            ) : (
              <div style={{ position: 'relative', marginBottom: '8px' }}>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '6px', color: '#374151' }}>
                  Campaign Name *
                </label>
                <input
                  type="text"
                  placeholder="Enter campaign name"
                  value={campaignName}
                  onChange={(e) => {
                    setCampaignName(e.target.value);
                    // Clear campaign name error when user starts typing
                    if (e.target.value.trim()) {
                      setErrors(prev => ({ ...prev, campaignName: '' }));
                    }
                  }}
                  style={{ 
                    width: '100%',
                    padding: '10px 12px', 
                    border: errors.campaignName ? '1px solid #ef4444' : '1px solid #e5e7eb', 
                    borderRadius: 8,
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                />
                {errors.campaignName && (
                  <div style={{ 
                    color: '#ef4444', 
                    fontSize: '12px', 
                    marginTop: '4px'
                  }}>
                    {errors.campaignName}
                  </div>
                )}
              </div>
            )}

            {/* ── AI Behaviour (per-campaign override) ─────────────────────
                Lets the user pin which skills/KB tags the AI should use when
                a recipient replies, and optionally suppress auto-replies. */}
            <CampaignAIBehaviourPanel
              skills={aiSkills}
              value={aiBehaviour}
              onChange={setAiBehaviour}
            />

            {!isMobile ? (
              <Tooltip 
                title={
                  !selectedAddressBook ? "Step 1: Select an address book" :
                  selectedContacts.length === 0 ? "Step 2: Select at least one contact" :
                  templateVariables.length > 0 && !completedSteps.variableMappingComplete ? "Step 3: Map all template variables" :
                  !campaignName.trim() ? "Step 4: Enter a campaign name" :
                  "All set! Click to create campaign"
                }
                placement="bottomRight"
              >
                <button 
                  onClick={handleCreateCampaign}
                  disabled={creatingCampaign || selectedContacts.length === 0 || !campaignName.trim() || (templateVariables.length > 0 && !completedSteps.variableMappingComplete)}
                  className={`broadcast-btn ${currentStep === 4 ? 'enabled' : 'disabled'}`}
                >
                  {creatingCampaign ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ 
                        width: '12px', 
                        height: '12px', 
                        border: '2px solid #ffffff', 
                        borderTop: '2px solid transparent', 
                        borderRadius: '50%', 
                        animation: 'spin 1s linear infinite' 
                      }}></div>
                      Creating...
                    </span>
                  ) : (
                    `Create Campaign (${selectedContacts.length} contacts)`
                  )}
                </button>
              </Tooltip>
            ) : (
              <button 
                onClick={handleCreateCampaign}
                disabled={creatingCampaign || selectedContacts.length === 0 || !campaignName.trim() || (templateVariables.length > 0 && !completedSteps.variableMappingComplete)}
                style={{
                  width: '100%',
                  padding: '12px 20px',
                  backgroundColor: (creatingCampaign || selectedContacts.length === 0 || !campaignName.trim() || (templateVariables.length > 0 && !completedSteps.variableMappingComplete)) ? '#9ca3af' : '#3b82f6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: '15px',
                  fontWeight: 600,
                  cursor: (creatingCampaign || selectedContacts.length === 0 || !campaignName.trim() || (templateVariables.length > 0 && !completedSteps.variableMappingComplete)) ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: (creatingCampaign || selectedContacts.length === 0 || !campaignName.trim() || (templateVariables.length > 0 && !completedSteps.variableMappingComplete)) ? 0.6 : 1
                }}
              >
                {creatingCampaign ? (
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                    <div style={{ 
                      width: '14px', 
                      height: '14px', 
                      border: '2px solid #ffffff', 
                      borderTop: '2px solid transparent', 
                      borderRadius: '50%', 
                      animation: 'spin 1s linear infinite' 
                    }}></div>
                    Creating Campaign...
                  </span>
                ) : (
                  `Create Campaign (${selectedContacts.length} contacts)`
                )}
              </button>
            )}
          </div>
          
          {/* Progress indicators and tips */}
          {!completedSteps.campaignNameEntered && !isMobile && (
            <div style={{ 
              fontSize: '12px', 
              color: '#ef4444', 
              textAlign: 'right'
            }}>
              • Enter a campaign name
            </div>
          )}
          
          {/* API Error Message */}
          {errors.api && (
            <div style={{ 
              color: '#ef4444', 
              fontSize: '14px', 
              backgroundColor: '#fef2f2', 
              padding: '8px 12px', 
              borderRadius: '6px',
              border: '1px solid #fecaca',
              maxWidth: isMobile ? '100%' : '400px',
              textAlign: isMobile ? 'center' : 'right'
            }}>
              {errors.api}
            </div>
          )}
        </div>
      </div>

      {/* Mobile Step Navigation */}
      {isMobile && (
        <div className="mobile-step-navigation">
          <div className="mobile-steps-container">
            {[
              { step: 0, label: 'Address Book', icon: 'bi-journal-bookmark' },
              { step: 1, label: 'Contacts', icon: 'bi-people' },
              { step: 2, label: 'Preview', icon: 'bi-eye' },
              { step: 3, label: 'Create', icon: 'bi-send' }
            ].map((item, index) => {
              // Determine if step is completed based on sequential progress
              const isStepCompleted = 
                (item.step === 0 && completedSteps.addressBookSelected) ||
                (item.step === 1 && completedSteps.addressBookSelected && completedSteps.contactsSelected) ||
                (item.step === 2 && completedSteps.addressBookSelected && completedSteps.contactsSelected && (templateVariables.length === 0 || completedSteps.variableMappingComplete)) ||
                (item.step === 3 && completedSteps.addressBookSelected && completedSteps.contactsSelected && (templateVariables.length === 0 || completedSteps.variableMappingComplete) && completedSteps.campaignNameEntered);
              
              // Check if step is accessible (previous step must be completed)
              const isStepAccessible = 
                item.step === 0 ||
                (item.step === 1 && completedSteps.addressBookSelected) ||
                (item.step === 2 && completedSteps.addressBookSelected && completedSteps.contactsSelected) ||
                (item.step === 3 && completedSteps.addressBookSelected && completedSteps.contactsSelected && (templateVariables.length === 0 || completedSteps.variableMappingComplete));
              
              return (
                <div 
                  key={item.step}
                  className={`mobile-step-item ${mobileStep === item.step ? 'active' : ''} ${isStepCompleted ? 'completed' : ''} ${!isStepAccessible ? 'disabled' : ''}`}
                  onClick={() => {
                    if (isStepAccessible) {
                      // Clear errors when navigating to an accessible step
                      setErrors(prev => ({
                        ...prev,
                        addressBook: '',
                        contacts: '',
                        variableMapping: '',
                        campaignName: ''
                      }));
                      setMobileStep(item.step);
                    } else {
                      // Show inline error based on missing step
                      if (item.step >= 1 && !completedSteps.addressBookSelected) {
                        setErrors(prev => ({
                          ...prev,
                          addressBook: 'Please select an address book first'
                        }));
                        setMobileStep(0);
                      } else if (item.step >= 2 && !completedSteps.contactsSelected) {
                        setErrors(prev => ({
                          ...prev,
                          contacts: 'Please select at least one contact first'
                        }));
                        setMobileStep(1);
                      } else if (item.step >= 3 && templateVariables.length > 0 && !completedSteps.variableMappingComplete) {
                        setErrors(prev => ({
                          ...prev,
                          variableMapping: 'Please map all template variables first'
                        }));
                        setMobileStep(2);
                      }
                    }
                  }}
                >
                  <div className="mobile-step-number">
                    {isStepCompleted ? (
                      <i className="bi bi-check"></i>
                    ) : (
                      item.step + 1
                    )}
                  </div>
                  <span className="mobile-step-label">{item.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Phase 2.5 — BYO Webhook upsell (only when plan supports it) */}
      {byoEnabled && (
        <div style={{ padding: '0 16px', marginBottom: 12 }}>
          <Alert
            type="info"
            showIcon
            message="Need to send to contacts that aren't in your address book?"
            description="Use the Advanced Builder to stream recipients from your own server (BYO Webhook) — perfect for one-off campaigns to large external audiences."
            action={
              <Button
                size="small"
                type="primary"
                onClick={() => navigate('/dashboard/broadcast/new', { state: { template, preferSource: 'byo_webhook' } })}
              >
                Open Advanced Builder
              </Button>
            }
            style={{ borderRadius: 10 }}
          />
        </div>
      )}

      {/* Main Content */}
      <div className={`broadcast-content ${isMobile ? 'mobile-view' : ''}`}>
        {/* Address Books Column */}
        <Tooltip
          title={currentStep === 0 && !isMobile ? "👉 Step 1: Select an address book to continue" : null}
          placement="right"
          open={currentStep === 0 && !isMobile}
          color="#3b82f6"
          overlayInnerStyle={{ fontSize: '13px', padding: '8px 12px' }}
        >
          <div 
            className={`address-books-column column ${isMobile && mobileStep !== 0 ? 'mobile-hidden' : ''}`}
            style={{
              border: currentStep === 0 && !isMobile ? '2px solid #3b82f6' : undefined,
              boxShadow: currentStep === 0 && !isMobile ? '0 0 8px rgba(59, 130, 246, 0.15)' : undefined,
              position: 'relative',
              transition: 'all 0.3s ease'
            }}
          >
          <div className="column-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 className="column-title">Address Books</h2>
              <span className="count-badge">{addressBooks.length}</span>
              {completedSteps.addressBookSelected && (
                <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span>
              )}
            </div>
            {errors.addressBook && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: isMobile ? '13px' : '12px',
                marginTop: '6px',
                padding: isMobile ? '8px 12px' : '0',
                backgroundColor: isMobile ? '#fef2f2' : 'transparent',
                borderRadius: isMobile ? '6px' : '0',
                border: isMobile ? '1px solid #fecaca' : 'none'
              }}>
                {errors.addressBook}
              </div>
            )}

          </div>
          
          <div className="search-container">
            <div className="search-box">
              <i className="bi bi-search search-icon"></i>
              <input
                type="text"
                placeholder="Search address books..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="address-books-list">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading address books...</p>
              </div>
            ) : addressBooks.length === 0 ? (
              <div className="empty-state">
                <div className="empty-content">
                  <i className="bi bi-folder-x empty-icon"></i>
                  <h3 className="empty-title">No Address Books Found</h3>
                  <p className="empty-description">Create an address book to get started.</p>
                </div>
              </div>
            ) : (
              addressBooks.map((book) => (
                <div
                  key={book.bookid}
                  onClick={() => handleAddressBookSelect(book)}
                  className={`address-book-item ${selectedAddressBook?.bookid === book.bookid ? 'selected' : ''}`}
                >
                  <img
                    src={`https://api.dicebear.com/7.x/initials/svg?seed=${book.bookname}&backgroundColor=3b82f6,10b981,f59e0b,ef4444,8b5cf6&textColor=ffffff`}
                    alt={book.bookname}
                    className="book-avatar"
                  />
                  <div className="book-info">
                    <h3 className="book-name">{book.bookname}</h3>
                    <div className="book-details">
                      <span className="book-contacts">{book.totaluser || 0} contacts</span>
                      <span className="book-date">{new Date(book.entrytime).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {selectedAddressBook?.bookid === book.bookid && (
                    <div className="selected-indicator">✓</div>
                  )}
                </div>
              ))
            )}
          </div>
          </div>
        </Tooltip>

        {/* WhatsApp Preview Column */}
        <div className={`preview-column column ${isMobile && mobileStep !== 2 ? 'mobile-hidden' : ''}`}>
          <div className="chat-header">
            <div className="chat-avatar whatsapp-avatar">
              <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="20" fill="#25D366"/>
                <path d="M20 10C17.79 10 16 11.79 16 14C16 16.21 17.79 18 20 18C22.21 18 24 16.21 24 14C24 11.79 22.21 10 20 10ZM20 20C16.67 20 10 21.67 10 25V28H30V25C30 21.67 23.33 20 20 20Z" fill="white"/>
              </svg>
            </div>
            <div className="chat-info">
              <h3 className="chat-name">{(() => {
                // Resolve company name from session storage (fallback to WhatsApp Business)
                let companyName = 'WhatsApp Business';
                try {
                  const usersStr = sessionStorage.getItem('user');
                  if (usersStr) {
                    const usersObj = JSON.parse(usersStr);
                    if (usersObj && usersObj.name) {
                      companyName = usersObj.name;
                    }
                  }
                } catch (err) {
                  // keep default
                }
                return companyName;
              })()}</h3>
              <p className="chat-status">Template Preview</p>
            </div>
            <span className="template-category">{template?.category || 'MARKETING'}</span>
          </div>

          <div className="chat-area">
            <div className="message-container">
              <div className="message-bubble">
                <div className="message-content">
                  {getPreviewContentWithMapping(template) || 'Select a template to preview the message content here. This is where your template message will appear.'}
                </div>
                <div className="message-footer">
                  <div className="message-time">
                    {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    <svg width="16" height="15" viewBox="0 0 16 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.879a.32.32 0 0 1-.484.033l-.358-.325a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033l6.272-8.048a.366.366 0 0 0-.063-.51zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.879a.32.32 0 0 1-.484.033L3.724 9.587a.319.319 0 0 0-.484.033l-.378.387a.32.32 0 0 0 .033.484l1.025.93a.32.32 0 0 0 .484-.033L10.676 3.84a.366.366 0 0 0-.063-.51z" fill="#4fc3f7"/>
                    </svg>
                  </div>
                </div>

                {/* Variable Mapping Controls */}
                {templateVariables.length > 0 && selectedAddressBook && (
                  <Tooltip
                    title={currentStep === 2 && !isMobile ? "👉 Step 3: Click here to map template variables to your address book columns" : null}
                    placement="bottom"
                    open={currentStep === 2 && !isMobile}
                    color="#3b82f6"
                    overlayInnerStyle={{ fontSize: '13px', padding: '8px 12px' }}
                  >
                    <div style={{ 
                      marginTop: 8,
                      border: currentStep === 2 && !isMobile ? '1.5px solid #3b82f6' : '1px solid transparent',
                      borderRadius: '8px',
                      padding: currentStep === 2 && !isMobile ? '8px' : '0',
                        boxShadow: currentStep === 2 && !isMobile ? '0 0 6px rgba(59, 130, 246, 0.15)' : 'none',
                        transition: 'all 0.3s ease'
                    }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button 
                        onClick={toggleVariableMapping}
                        className={`map-variables-btn-inline ${showVariableMapping ? 'active' : ''}`}
                        title="Map template variables to address book columns"
                      >
                        <i className="bi bi-link-45deg"></i>
                        <span className="variable-count">{templateVariables.length}</span>
                        <i className={`bi bi-chevron-${showVariableMapping ? 'up' : 'down'} chevron-icon`}></i>
                      </button>
                      {completedSteps.variableMappingComplete && (
                        <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span>
                      )}
                    </div>
                    {errors.variableMapping && (
                      <div style={{ 
                        color: '#ef4444', 
                        fontSize: isMobile ? '13px' : '12px',
                        marginTop: '6px',
                        padding: isMobile ? '8px 12px' : '0',
                        backgroundColor: isMobile ? '#fef2f2' : 'transparent',
                        borderRadius: isMobile ? '6px' : '0',
                        border: isMobile ? '1px solid #fecaca' : 'none'
                      }}>
                        {errors.variableMapping}
                      </div>
                    )}

                    </div>
                  </Tooltip>
                )}
                 
                 {getTemplateButtons(template).length > 0 && (
                  <div className="template-buttons">
                    {getTemplateButtons(template).map((button, index) => (
                      <button key={index} className="template-button">
                        {button.text}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>


          </div>
        </div>

        {/* Contacts Column */}
        <Tooltip
          title={currentStep === 1 && !isMobile ? "👉 Step 2: Select at least one contact" : null}
          placement="left"
          open={currentStep === 1 && !isMobile}
          color="#3b82f6"
          overlayInnerStyle={{ fontSize: '13px', padding: '8px 12px' }}
        >
          <div 
            className={`contacts-column column ${isMobile && mobileStep !== 1 ? 'mobile-hidden' : ''}`}
            style={{
              border: currentStep === 1 && !isMobile ? '2px solid #3b82f6' : undefined,
              boxShadow: currentStep === 1 && !isMobile ? '0 0 8px rgba(59, 130, 246, 0.15)' : undefined,
              position: 'relative',
              transition: 'all 0.3s ease'
            }}
          >
          <div className="column-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <h2 className="column-title">Contacts</h2>
              {selectedAddressBook && (
                <span className="selected-badge">{selectedContacts.length} selected</span>
              )}
              {completedSteps.contactsSelected && (
                <span style={{ color: '#10b981', fontSize: '14px' }}>✓</span>
              )}
            </div>
            {errors.contacts && (
              <div style={{ 
                color: '#ef4444', 
                fontSize: isMobile ? '13px' : '12px',
                marginTop: '6px',
                padding: isMobile ? '8px 12px' : '0',
                backgroundColor: isMobile ? '#fef2f2' : 'transparent',
                borderRadius: isMobile ? '6px' : '0',
                border: isMobile ? '1px solid #fecaca' : 'none'
              }}>
                {errors.contacts}
              </div>
            )}

          </div>

          {!selectedAddressBook ? (
            <div className="empty-state">
              <div className="empty-content">
                <i className="bi bi-people empty-icon"></i>
                <h3 className="empty-title">No Address Book Selected</h3>
                <p className="empty-description">Select an address book to view and choose contacts for your broadcast.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="filters-section">
                <input
                  type="text"
                  placeholder="Search contacts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="filter-input"
                />
                <Select
                  mode="multiple"
                  placeholder="Select labels to filter"
                  value={labelFilter}
                  onChange={handleLabelFilterChange}
                  style={{ width: '100%' }}
                  allowClear
                  maxTagCount="responsive"
                  optionLabelProp="label"
                >
                  {/* Predefined labels with colors */}
                  {predefinedLabels.map((label) => (
                    <Select.Option key={label.value} value={label.value}>
                      <Space size={4}>
                        <span style={{ color: label.color, fontSize: '14px' }}>●</span>
                        <span>{label.label}</span>
                      </Space>
                    </Select.Option>
                  ))}
                  
                  {/* Custom labels extracted from contacts */}
                  {availableLabels
                    .filter(label => !predefinedLabels.some(predefined => predefined.value === label.toLowerCase()))
                    .map((label) => (
                      <Select.Option key={label} value={label}>
                        <Space size={4}>
                          <span style={{ color: '#8884d8', fontSize: '14px' }}>●</span>
                          <span>{label}</span>
                        </Space>
                      </Select.Option>
                    ))}
                </Select>
              </div>

              <div className="select-all-section">
                <label className="select-all-label">
                  <input
                    type="checkbox"
                    checked={filteredContacts.length > 0 && filteredContacts.every(fc => selectedContacts.some(sc => sc.userid === fc.userid))}
                    onChange={handleSelectAll}
                    className="select-all-checkbox"
                  />
                  Select All ({filteredContacts.length} contacts)
                </label>
              </div>

              <div className="contacts-list">
                {contactsLoading ? (
                  <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading contacts...</p>
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-content">
                      <i className="bi bi-person-x empty-icon"></i>
                      <h3 className="empty-title">No Contacts Found</h3>
                      <p className="empty-description">No contacts match your current search criteria.</p>
                    </div>
                  </div>
                ) : (
                  filteredContacts.map((contact) => (
                    <div 
                      key={contact.userid} 
                      className="contact-item"
                      onClick={() => isMobile && handleContactSelect(contact)}
                      style={{ cursor: isMobile ? 'pointer' : 'default' }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedContacts.some(c => c.userid === contact.userid)}
                        onChange={() => !isMobile && handleContactSelect(contact)}
                        className="contact-checkbox"
                        style={{ pointerEvents: isMobile ? 'none' : 'auto' }}
                      />
                      <div className="contact-avatar">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="16" cy="16" r="16" fill="#6B7280"/>
                          <path d="M16 8C13.79 8 12 9.79 12 12C12 14.21 13.79 16 16 16C18.21 16 20 14.21 20 12C20 9.79 18.21 8 16 8ZM16 18C12.67 18 6 19.67 6 23V26H26V23C26 19.67 19.33 18 16 18Z" fill="white"/>
                        </svg>
                      </div>
                      <div className="contact-info">
                        <h4 className="contact-name">{contact.username}</h4>
                        <p className="contact-phone">+{contact.countrycode} {contact.mobileno}</p>
                        {contact.label && (
                          <span className="contact-label">{contact.label}</span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {!loading && filteredContacts.length > 0 && (
                <div className="pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
          </div>
        </Tooltip>

        {/* Mobile Create Campaign Step */}
        {isMobile && mobileStep === 3 && (
          <div className="mobile-create-campaign-column column">
            <div className="column-header">
              <h2 className="column-title">Create Campaign</h2>
            </div>
            <div className="mobile-create-content">
              <div className="mobile-summary-section">
                <h3 className="summary-title">Campaign Summary</h3>
                <div className="summary-item">
                  <span className="summary-label">Template:</span>
                  <span className="summary-value">{template?.name || template?.templatename || 'Selected Template'}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Address Book:</span>
                  <span className="summary-value">{selectedAddressBook?.bookname || 'Not Selected'}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Selected Contacts:</span>
                  <span className="summary-value" style={{ color: '#10b981', fontWeight: 600 }}>{selectedContacts.length} contacts</span>
                </div>
                {templateVariables.length > 0 && (
                  <div className="summary-item">
                    <span className="summary-label">Variable Mapping:</span>
                    <span className="summary-value" style={{ color: completedSteps.variableMappingComplete ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                      {completedSteps.variableMappingComplete ? '✓ Complete' : '✗ Incomplete'}
                    </span>
                  </div>
                )}
              </div>
              
              {templateVariables.length > 0 && !completedSteps.variableMappingComplete && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: 8,
                  padding: '12px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>
                  <i className="bi bi-exclamation-triangle-fill" style={{ color: '#ef4444', fontSize: '16px' }}></i>
                  <span style={{ color: '#ef4444', fontSize: '13px' }}>
                    Go back to Preview step and map all variables before creating the campaign.
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <div className="mobile-bottom-navigation">
          <button 
            className="mobile-nav-btn mobile-nav-prev"
            onClick={() => {
              // Clear errors when going back
              setErrors(prev => ({
                ...prev,
                addressBook: '',
                contacts: '',
                variableMapping: '',
                campaignName: ''
              }));
              setMobileStep(prev => Math.max(0, prev - 1));
            }}
            disabled={mobileStep === 0}
          >
            <i className="bi bi-chevron-left"></i>
            Previous
          </button>
          <div className="mobile-nav-indicator">
            Step {mobileStep + 1} of 4
          </div>
          <button 
            className="mobile-nav-btn mobile-nav-next"
            onClick={() => {
              // Clear previous errors first
              setErrors(prev => ({
                ...prev,
                addressBook: '',
                contacts: '',
                variableMapping: '',
                campaignName: ''
              }));
              
              // Validate current step before moving to next
              if (mobileStep === 0 && !completedSteps.addressBookSelected) {
                setErrors(prev => ({
                  ...prev,
                  addressBook: 'Please select an address book to continue'
                }));
                return;
              }
              if (mobileStep === 1 && !completedSteps.contactsSelected) {
                setErrors(prev => ({
                  ...prev,
                  contacts: 'Please select at least one contact to continue'
                }));
                return;
              }
              if (mobileStep === 2 && templateVariables.length > 0 && !completedSteps.variableMappingComplete) {
                setErrors(prev => ({
                  ...prev,
                  variableMapping: 'Please map all template variables to continue'
                }));
                return;
              }
              setMobileStep(prev => Math.min(3, prev + 1));
            }}
            disabled={mobileStep === 3}
          >
            Next
            <i className="bi bi-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Variable Mapping Modal - Rendered at root level for proper positioning */}
      {showVariableMapping && templateVariables.length > 0 && selectedAddressBook && (
        <>
          <div 
            className="variable-mapping-overlay"
            onClick={() => setShowVariableMapping(false)}
          />
          <div className="variable-mapping-modal-container">
            <div className="variable-mapping-modal-header">
              <h3 className="variable-mapping-modal-title">Map Template Variables</h3>
              <button 
                className="variable-mapping-modal-close"
                onClick={() => setShowVariableMapping(false)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="variable-mapping-modal-body">
              {templateVariables.map((variable, index) => (
                <div key={index} className="variable-mapping-item">
                  <div className="variable-mapping-label">
                    <span className="variable-mapping-tag">{variable}</span>
                    <span className="variable-mapping-arrow">→</span>
                  </div>
                  <Select
                    className="variable-mapping-select"
                    placeholder="Select column..."
                    value={variableMapping[variable] || undefined}
                    onChange={(value) => handleVariableMapping(variable, value)}
                    style={{ width: '100%' }}
                    getPopupContainer={(triggerNode) => {
                      const modalContainer = document.querySelector('.variable-mapping-modal-container');
                      return modalContainer || triggerNode.parentElement;
                    }}
                    dropdownStyle={{ 
                      maxHeight: 200, 
                      overflow: 'auto',
                      maxWidth: isMobile ? 'calc(100vw - 64px)' : '400px'
                    }}
                    dropdownMatchSelectWidth={isMobile ? false : true}
                    virtual={false}
                  >
                    {addressBookColumns.map((column, idx) => (
                      <Select.Option key={idx} value={column.value}>{column.label}</Select.Option>
                    ))}
                  </Select>
                </div>
              ))}

              {/* Dynamic media header URL mapping — shown only when template has a media header */}
              {templateHeaderType && (
                <div className="variable-mapping-item" style={{ borderTop: '1px solid #e5e7eb', paddingTop: 12, marginTop: 4 }}>
                  <div className="variable-mapping-label" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className="variable-mapping-tag" style={{ background: templateHeaderType === 'document' ? '#f0fdf4' : templateHeaderType === 'image' ? '#eff6ff' : '#faf5ff', color: templateHeaderType === 'document' ? '#166534' : templateHeaderType === 'image' ? '#1d4ed8' : '#6b21a8', border: '1px solid currentColor' }}>
                      {templateHeaderType === 'document' ? '📄' : templateHeaderType === 'image' ? '🖼️' : '🎥'} Header {templateHeaderType} URL
                    </span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>Map to the column containing the {templateHeaderType} link (must be https://)</span>
                  </div>
                  <span className="variable-mapping-arrow">→</span>
                  <Select
                    className="variable-mapping-select"
                    placeholder="Select URL column..."
                    value={headerMediaColumn || undefined}
                    onChange={(value) => setHeaderMediaColumn(value)}
                    style={{ width: '100%' }}
                    allowClear
                    getPopupContainer={(triggerNode) => {
                      const modalContainer = document.querySelector('.variable-mapping-modal-container');
                      return modalContainer || triggerNode.parentElement;
                    }}
                    dropdownStyle={{ 
                      maxHeight: 200, 
                      overflow: 'auto',
                      maxWidth: isMobile ? 'calc(100vw - 64px)' : '400px'
                    }}
                    dropdownMatchSelectWidth={isMobile ? false : true}
                    virtual={false}
                  >
                    {addressBookColumns.map((column, idx) => (
                      <Select.Option key={idx} value={column.value}>{column.label}</Select.Option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
            <div className="variable-mapping-modal-footer">
              <button 
                className="variable-mapping-done-btn"
                onClick={() => setShowVariableMapping(false)}
              >
                {completedSteps.variableMappingComplete
                  ? 'Done ✓'
                  : `Map ${Object.keys(variableMapping).filter(k => variableMapping[k]).length}/${templateVariables.length} variables${templateHeaderType && !headerMediaColumn ? ' + header URL' : ''}`}
              </button>
            </div>
          </div>
        </>
      )}


    </div>
  );
};

export default NewTemplateBroadcastPage;